import { useEffect, useMemo, useState } from 'react'
import { useGetSurgeriesQuery } from 'lib/apollo/hooks'
import dayjs from 'lib/dayjs'
import { Option, Surgery } from 'common/types'
import { useDisablePolling } from 'lib/useDisablePolling'
import { useProcedureListTableLogic } from 'components/ProcedureList/ProcedureListTable.logic'
import { formatSurgeryProviderName } from 'lib/utils/formatSurgeryProviderName'
import { useAddTrayContext } from 'views/TrayManagementLayout/AddTray/AddTray.context'
import { useGetSurgeons } from 'lib/services/api/reports-service/preference-card/getSurgeons'
import { useApolloClients } from 'lib/apollo/apolloProvider'
import { useEnterpriseStore } from 'store'

/**
 * Hook to manage the logic for tray case details.
 * @returns {object} Object containing tray case details logic.
 */
const useTrayCaseDetailsLogic = () => {
  const disablePolling = useDisablePolling()
  const {
    trays,
    setTrays,
    traySurgeryId,
    setTraySurgeryId,
    setTraySurgery,
    selectedSurgeon,
    setSelectedSurgeon,
    traySurgery,
    updateCurrentTrayItem,
    currentTrayIndex,
  } = useAddTrayContext()

  const [beforeDate, setBeforeDate] = useState<Date>(
    dayjs().endOf('day').add(1, 'month').toDate()
  )
  const [afterDate, setAfterDate] = useState<Date>(
    dayjs().startOf('day').toDate()
  )

  const { servicesClient: client } = useApolloClients()
  const { selectedDepartment, isDepartmentsLoaded } = useEnterpriseStore()

  const {
    data,
    loading: apolloLoading,
    error,
  } = useGetSurgeriesQuery(
    {
      before: beforeDate,
      after: afterDate,
      status: undefined,
      limit: 0,
      isFromDTM: true,
      departmentId: selectedDepartment?.id,
    },
    {
      disablePolling,
      client,
      skip: !isDepartmentsLoaded,
    }
  )

  const loading = !isDepartmentsLoaded || apolloLoading

  const surgeryList = useMemo(
    () => (data ? data.getSurgeries?.results : []),
    [data]
  )

  const isSelectedSurgeryNotInDateRange = useMemo(() => {
    return !surgeryList.some((surgery: Surgery) => {
      const surgeryDate = surgery.procedures
        ?.map((proc) => proc.dateTime)
        .filter(Boolean)
        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0]

      return (
        surgery._id === traySurgeryId &&
        dayjs(surgeryDate).isBefore(dayjs(beforeDate)) &&
        dayjs(surgeryDate).isAfter(dayjs(afterDate))
      )
    })
  }, [surgeryList, traySurgeryId, beforeDate, afterDate])

  const { isNurse, sortedSurgeries: surgeries = [] } =
    useProcedureListTableLogic({
      events: surgeryList,
      showDate: true,
      sortByDate: true,
    })

  useEffect(() => {
    if (currentTrayIndex >= 0) {
      updateCurrentTrayItem()
    } else if (trays.length > 0) {
      // update surgery of last tray
      const lastTray = trays[trays.length - 1]
      setTrays((prevTrays) => {
        const newTrays = [...prevTrays]
        newTrays[newTrays.length - 1] = { ...lastTray, surgery: traySurgery }
        return newTrays
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traySurgeryId, traySurgery])

  /**
   * Handles changes to the before date.
   * @param {Date} date - The new before date.
   */
  const handleBeforeDateChange = (date: Date) => {
    setBeforeDate(dayjs(date).endOf('day').toDate())
  }

  /**
   * Handles changes to the after date.
   * @param {Date} date - The new after date.
   */
  const handleAfterDateChange = (date: Date) => {
    setAfterDate(dayjs(date).startOf('day').toDate())
  }

  /**
   * Handles changes to the surgeon filter.
   * @param {Option} value - The selected surgeon option.
   */
  const handleSurgeonFilterChange = (value: Option) => {
    setSelectedSurgeon(value.name)
  }

  /**
   * Clears the surgeon filter.
   */
  const handleClearSurgeonFilter = () => {
    setSelectedSurgeon('')
  }

  /**
   * Handles changes to the selected tray surgery.
   * @param {Surgery} surgery - The selected surgery.
   */
  const handleTraySurgeryChange = (surgery: Surgery) => {
    if (surgery._id === traySurgeryId) {
      setTraySurgeryId('')
      return
    }

    setTraySurgeryId(surgery._id)
    setTraySurgery(surgery)
  }

  /**
   * Checks if a surgery is selected.
   * @param {Surgery['_id']} surgeryId - The ID of the surgery to check.
   * @returns {boolean} True if the surgery is selected, false otherwise.
   */
  const isSurgerySelected = (surgeryId: Surgery['_id']) =>
    surgeryId === traySurgeryId

  const { data: surgeonsData, isLoading: surgeonsLoading } = useGetSurgeons()

  const surgeonOptions = useMemo(() => {
    return surgeonsData
      ? surgeonsData.surgeons.map((surgeon) => ({
          id: `${surgeon.lastName}, ${surgeon.firstName.charAt(0)}`,
          name: `${surgeon.lastName}, ${surgeon.firstName.charAt(0)}`,
        }))
      : []
  }, [surgeonsData])

  /**
   * Filters surgeries by the surgeon filter.
   * @param {Surgery[]} surgeries - The list of surgeries.
   * @param {string | undefined} surgeonFilter - The surgeon filter.
   * @returns {Surgery[]} The filtered list of surgeries.
   */
  const filterSurgeriesBySurgeon = (
    surgeries: Surgery[],
    surgeonFilter: string | undefined
  ): Surgery[] => {
    return surgeries
      ? surgeries.filter((surgery) =>
          surgeonFilter
            ? formatSurgeryProviderName(surgery).includes(surgeonFilter)
            : true
        )
      : []
  }

  const filteredSurgeries = useMemo(
    () => filterSurgeriesBySurgeon(surgeries, selectedSurgeon),
    [surgeries, selectedSurgeon]
  )

  return {
    surgeries,
    loading,
    error,
    isNurse,
    afterDate,
    beforeDate,
    handleBeforeDateChange,
    handleAfterDateChange,
    surgeonOptions,
    selectedSurgeon,
    surgeonsLoading,
    handleSurgeonFilterChange,
    handleClearSurgeonFilter,
    filteredSurgeries,
    handleTraySurgeryChange,
    isSurgerySelected,
    isSelectedSurgeryNotInDateRange,
    traySurgery,
  }
}

export default useTrayCaseDetailsLogic
